import { useState } from "react";
import { MessageSquareHeart } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const TYPES = [["bug", "Bug"], ["idea", "Idea"], ["praise", "Praise"], ["other", "Other"]];
const SHORT = "Tell us a bit more.";

export const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("idea");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) return setStatus({ ok: false, msg: SHORT });
    setBusy(true);
    setStatus(null);
    try {
      const { data } = await api.post("/feedback", { type, message, page_url: window.location.href });
      setStatus({ ok: true, msg: data.message });
      setMessage("");
    } catch (err) {
      setStatus({ ok: false, msg: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setStatus(null); }}>
      <DialogTrigger asChild>
        <button data-testid="feedback-open-button"
          className="glass fixed bottom-5 right-5 z-40 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#E9C98B]/45 px-4 py-2.5 text-sm text-[#F3DDAE] shadow-[0_18px_50px_-18px_rgba(233,201,139,0.55)] transition-transform duration-500 hover:-translate-y-0.5">
          <MessageSquareHeart className="h-4 w-4 text-[#E9C98B]" />Feedback
        </button>
      </DialogTrigger>
      <DialogContent data-testid="feedback-dialog" className="w-[calc(100%-2rem)] max-w-lg rounded-3xl border border-[#E9C98B]/35 bg-[#0B0C18]/95 backdrop-blur-xl shadow-[0_40px_120px_-40px_rgba(233,201,139,0.35)] sm:rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">Beta feedback</DialogTitle>
          <DialogDescription>Bugs, ideas, praise, anything. It goes straight to the founder.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
          <div className="grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Feedback type">
            {TYPES.map(([v, l]) => (
              <button type="button" key={v} role="radio" aria-checked={type === v} onClick={() => setType(v)} data-testid={`feedback-type-${v}`}
                className={`rounded-full border px-2 py-1.5 text-sm transition-colors ${type === v ? "border-[#E9C98B]/60 bg-[#E9C98B]/10 text-[#F3DDAE]" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
          <Textarea rows={5} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What's on your mind?"
            data-testid="feedback-message-input" className="rounded-2xl border-white/10 bg-white/[0.04]" />
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-xs text-muted-foreground">{message.length}/2000</span>
            <Button type="submit" disabled={busy} data-testid="feedback-submit-button" className="btn-aurora rounded-full px-6">{busy ? "Sending…" : "Send feedback"}</Button>
          </div>
          {status && (
            <p data-testid={status.ok ? "feedback-success" : "feedback-error"} className={`text-sm ${status.ok ? "text-emerald-300" : "text-rose-300"}`}>{status.msg}</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
